import InfoIcon from "@mui/icons-material/Info";
import { Box, FormControl, MenuItem, Typography } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { agentConnectorConfigsKeys } from "../../../api/queryKeys";
import ContainedButton from "../../../components/buttons/ContainedButton";
import { StyledSelect } from "../../../components/forms/styles/formFieldStyles";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import SvgToIcon from "../../../icons/SvgToIcon";
import { updatedXTimeAgo } from "../../../utils/timestamp";
import { ReactComponent as ChevronDownIcon } from "../../stack/environment/icons/chevron-down.svg";
import { icons } from "../../stack/environment/icons/iconExport";
import DigestWithCopy from "./DigestWithCopy";
import OnPremCollapseSection from "./OnPremCollapseSection";

export default function ConfigurationFileSection({
  selectedVersion = "",
  onChangeVersion,
}) {
  const colors = useColors();
  const { getAgentConnectorConfigs, downloadAgentConnectorConfig, trackEvent } =
    useAPI();

  const { data: configArtifacts = [], isSuccess } = useQuery({
    queryKey: agentConnectorConfigsKeys.all,
    queryFn: async () => {
      const { data } = await getAgentConnectorConfigs();
      return Array.isArray(data) ? data : [];
    },
  });

  const [configVersion, setConfigVersion] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (
      !selectedVersion &&
      isSuccess &&
      Array.isArray(configArtifacts) &&
      configArtifacts.length > 0
    ) {
      setConfigVersion(configArtifacts[0].version);
    }
  }, [isSuccess, configArtifacts, selectedVersion]);

  const latestConfigVersion = useMemo(
    () => configArtifacts[0]?.version || "",
    [configArtifacts]
  );
  const effectiveConfigVersion = useMemo(
    () => selectedVersion || configVersion || latestConfigVersion,
    [selectedVersion, configVersion, latestConfigVersion]
  );
  const selectedConfig = useMemo(() => {
    return (
      configArtifacts.find((a) => a.version === effectiveConfigVersion) ||
      configArtifacts[0] ||
      {}
    );
  }, [configArtifacts, effectiveConfigVersion]);

  const handleVersionChange = useCallback(
    (e) => {
      const v = e.target.value;
      setConfigVersion(v);
      if (onChangeVersion) onChangeVersion(v);
    },
    [onChangeVersion]
  );

  const handleDownload = useCallback(async () => {
    trackEvent(
      `User clicked on download agent connector config button for version ${effectiveConfigVersion}.`
    );
    try {
      setIsDownloading(true);
      const configName = selectedConfig.configName || "";
      const version = effectiveConfigVersion || "";
      const fileTypeLower = (selectedConfig.fileType || "").toLowerCase();
      if (!configName || !version || !fileTypeLower) {
        Toast.error("Missing configuration details to download.");
        return;
      }
      const filename = `${configName}-${version}.${fileTypeLower}`;
      const { data } = await downloadAgentConnectorConfig(
        configName,
        version,
        fileTypeLower
      );
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      Toast.success("Configuration File downloaded successfully.");
    } catch (e) {
      Toast.error("Failed to download Configuration File.");
    } finally {
      setIsDownloading(false);
    }
  }, [
    selectedConfig,
    effectiveConfigVersion,
    downloadAgentConnectorConfig,
    trackEvent,
  ]);

  return (
    <OnPremCollapseSection
      title="Download Configuration File"
      defaultCollapsed={false}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pl: 4 }}>
        <Typography variant="body1">
          Includes your connector’s environment variables and Helm values
          required for configuration.
        </Typography>

        <FormControl size="small" sx={{ width: 360 }}>
          <Typography variant="body2" sx={{ color: colors.text_secondary }}>
            Recent versions
          </Typography>
          <StyledSelect
            labelId={"image-version-label"}
            displayEmpty
            value={effectiveConfigVersion}
            IconComponent={ChevronDownIcon}
            onChange={handleVersionChange}
          >
            {configArtifacts.map((a) => (
              <MenuItem key={a.version} value={a.version}>
                <Typography variant="caption">
                  {`${a.configName}-${a.version}`}
                  {a.version === latestConfigVersion ? " (latest)" : ""}
                </Typography>
              </MenuItem>
            ))}
          </StyledSelect>
          {!isSuccess ? (
            <FormHelperText sx={{ color: colors.helper_text }}>
              <Box display="flex" alignItems="center" gap={1}>
                <LoadingIcon size={12} />
                Loading configuration info...
              </Box>
            </FormHelperText>
          ) : configVersion ? (
            <FormHelperText sx={{ color: colors.helper_text }}>
              <Box display="flex" alignItems="center" gap={1}>
                <InfoIcon sx={{ width: "12px", height: "12px" }} />
                {`${selectedConfig.fileType || "YAML"} · ${
                  selectedConfig.size || "0 KB"
                } · ${
                  selectedConfig.lastUpdated
                    ? updatedXTimeAgo(selectedConfig.lastUpdated)
                    : "N/A"
                }`}
              </Box>
            </FormHelperText>
          ) : null}
        </FormControl>

        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text_secondary }}>
              Digest (md5)
            </Typography>
            <DigestWithCopy digest={selectedConfig.digest || ""} />
          </Box>
        </Box>

        <ContainedButton
          color="primary"
          size="small"
          onClick={handleDownload}
          disabled={isDownloading || !isSuccess || !effectiveConfigVersion}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {isDownloading ? (
              <LoadingIcon size={14} />
            ) : (
              <SvgToIcon IconLogo={icons.ActionDownloadIcon} width="18px" />
            )}
            <Typography variant="overline">
              {isDownloading ? "Downloading..." : "Download Config"}
            </Typography>
          </Box>
        </ContainedButton>
      </Box>
    </OnPremCollapseSection>
  );
}
